import { getDefaultConfig } from "connectkit";
import { Chain, createClient, defineChain, http, isAddress } from "viem";
import { privateKeyToAccount } from "viem/accounts";
import { mainnet } from "viem/chains";
import { createConfig, createStorage } from "wagmi";
import { mock } from "wagmi/connectors";
import { getChainTransport } from "./chain-config";
import { chains } from "./chains";

// Re-export for backwards compatibility
export { chains };

function drpcRpcUrl(chainName: string) {
  const drpcKey = import.meta.env.VITE_DRPC_KEY;
  if (drpcKey) {
    return `https://lb.drpc.org/ogrpc?network=${chainName}&dkey=${drpcKey}`;
  }

  // E2E / CI: fork or custom RPC for mainnet (Playwright sets VITE_RPC_URL → Anvil)
  if (chainName === "ethereum" && import.meta.env.VITE_RPC_URL) {
    return import.meta.env.VITE_RPC_URL;
  }

  const chainKey =
    chainName === "ethereum" ? "mainnet" : (chainName as keyof typeof chains);
  const chain = chainName === "ethereum" ? chains.mainnet : chains[chainKey];
  const publicHttp = chain?.rpcUrls?.default?.http?.[0];
  if (publicHttp) {
    return publicHttp;
  }

  console.warn(
    `VITE_DRPC_KEY not set and no public RPC fallback for ${chainName}`,
  );
  return "";
}

export const config = createConfig(
  getDefaultConfig({
    chains: Object.values(chains) as unknown as readonly [Chain, ...Chain[]],
    transports: {
      [chains.mainnet.id]: getChainTransport(
        chains.mainnet.id,
        drpcRpcUrl("ethereum"),
      ),
      [chains.optimism.id]: getChainTransport(
        chains.optimism.id,
        drpcRpcUrl("optimism"),
      ),
      [chains.arbitrum.id]: getChainTransport(
        chains.arbitrum.id,
        drpcRpcUrl("arbitrum"),
      ),
      [chains.sonic.id]: getChainTransport(
        chains.sonic.id,
        drpcRpcUrl("sonic"),
      ),
      [chains.base.id]: getChainTransport(chains.base.id, drpcRpcUrl("base")),
      [chains.megaethTestnet.id]: getChainTransport(
        chains.megaethTestnet.id,
        chains.megaethTestnet.rpcUrls.default.http[0],
      ),
      [chains.avalanche.id]: getChainTransport(
        chains.avalanche.id,
        drpcRpcUrl("avalanche"),
      ),
      [chains.monadTestnet.id]: getChainTransport(
        chains.monadTestnet.id,
        chains.monadTestnet.rpcUrls.default.http[0],
      ),
      [chains.bsc.id]: getChainTransport(chains.bsc.id, drpcRpcUrl("bsc")),
      [chains.worldchain.id]: getChainTransport(
        chains.worldchain.id,
        drpcRpcUrl("worldchain"),
      ),
      [chains.etherlink.id]: getChainTransport(
        chains.etherlink.id,
        chains.etherlink.rpcUrls.default.http[0],
      ),
      [chains.hemi.id]: getChainTransport(chains.hemi.id, drpcRpcUrl("hemi")),
      [chains.lisk.id]: getChainTransport(chains.lisk.id, drpcRpcUrl("lisk")),
      [chains.berachain.id]: getChainTransport(
        chains.berachain.id,
        drpcRpcUrl("berachain"),
      ),
      [chains.plasma.id]: getChainTransport(
        chains.plasma.id,
        drpcRpcUrl("plasma"),
      ),
      [chains.ink.id]: getChainTransport(chains.ink.id, drpcRpcUrl("ink")),
    },

    walletConnectProjectId: import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || "",

    // Required App Info
    appName: "Gearbox",

    // Optional App Info
    appDescription: "Gearbox",
    appUrl: "https://gearbox.fi",
    appIcon: "https://static.gearbox.fi/logo/logo_symbol.png",

    // Add persistence configuration
    storage: createStorage({
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
    }),
  }),
);

/**
 * Creates a wagmi config with a mock connector for e2e testing.
 * Accepts a private key (local signing) or an address (RPC-delegated signing via Anvil impersonation).
 */
export function createMockConfig(addressOrPkey: `0x${string}`) {
  const account = isAddress(addressOrPkey)
    ? addressOrPkey
    : privateKeyToAccount(addressOrPkey);
  const address = typeof account === "string" ? account : account.address;
  const rpcUrl = import.meta.env.VITE_RPC_URL || "http://127.0.0.1:8545";

  /**
   * wagmi's mock connector forwards RPC methods (eth_signTypedData_v4, eth_sendTransaction, etc.)
   * to chain.rpcUrls.default.http[0]. Stock mainnet uses https://eth.merkle.io which doesn't
   * support wallet methods — point the chain at the local Anvil RPC instead.
   */
  const mockMainnet = defineChain({
    ...mainnet,
    rpcUrls: {
      default: {
        http: [rpcUrl],
      },
    },
  });

  return createConfig({
    chains: [mockMainnet],
    connectors: [mock({ accounts: [address] })],
    client: ({ chain }) =>
      createClient({
        account,
        chain,
        transport: http(rpcUrl),
      }),
    storage: createStorage({
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
    }),
  });
}

// Ensure the config is properly exported for use in the app
declare module "wagmi" {
  interface Register {
    config: typeof config;
  }
}
